import * as RadixScrollArea from '@radix-ui/react-scroll-area';
import * as RadixSelect from '@radix-ui/react-select';
import classNames from 'classnames';
import React, { useId } from 'react';
import { notNil } from '../../core/helpers/is-nil';
import { popoverContainers } from '../../core/popovers/popover-containers';
import { useAccentBgContext } from '../accent-bg-context/accent-bg-context';
import { FormHelperText } from '../form-helper-text/form-helper-text';
import { FormInlineNotification, InlineNotification } from '../form-inline-notification/form-inline-notification';
import { FormLabel } from '../form-label/form-label';
import { Icon } from '../icon/icon';
import { Radio } from '../radio/radio';
import { RadioIndicator } from '../radio/radio-indicator/radio-indicator';
import { Spacer } from '../spacer/spacer';
import { Typography } from '../typography/typography';
import styles from './select.module.scss';

export type SelectOption<TValue = string> = { value: TValue; label: string };

type Props = {
  ariaLabel: string;
  label?: string;
  placeholder: string;
  isDirty?: boolean;
  options: SelectOption[];
  mandatory?: boolean;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  inlineNotification?: InlineNotification;
  onChange?: (newValue: string) => void;
  helperMsg?: string;
  portalContainer?: HTMLElement;
  tabIndex?: number;
};

export const Select: React.FC<Props> = ({
  ariaLabel,
  label,
  isDirty = false,
  placeholder,
  options,
  mandatory,
  value,
  defaultValue,
  onChange,
  disabled = false,
  inlineNotification,
  helperMsg,
  portalContainer,
  tabIndex,
}) => {
  const { isAccentBackground } = useAccentBgContext();
  const selectId = useId();
  const helperId = useId();

  const hasLabel = notNil(label);
  const hasHelper = notNil(helperMsg);
  const hasInlineNotification = notNil(inlineNotification);
  const hasError = notNil(inlineNotification) && inlineNotification.type === 'error';

  const handleValueChange = (value: string) => {
    onChange?.(value);
  };

  return (
    <div className={classNames(styles.root, { [styles.accent]: isAccentBackground })}>
      {hasLabel ? (
        <FormLabel disabled={disabled} htmlFor={selectId} mandatory={mandatory}>
          {label}
        </FormLabel>
      ) : null}
      <div className={styles.innerContainer}>
        <RadixSelect.Root
          defaultValue={defaultValue}
          disabled={disabled}
          value={value ?? undefined}
          onValueChange={handleValueChange}
        >
          <RadixSelect.Trigger
            aria-describedby={hasHelper ? helperId : undefined}
            aria-invalid={hasError}
            aria-label={ariaLabel}
            tabIndex={tabIndex}
            className={classNames(styles.trigger, {
              [styles.accent]: isAccentBackground,
              [styles.disabled]: disabled,
              [styles.isDirty]: isDirty,
            })}
          >
            <RadixSelect.Value
              placeholder={
                <Typography color={disabled ? 'anthrazit-50' : 'anthrazit-75'} variant="maintext">
                  {placeholder}
                </Typography>
              }
            />
            <RadixSelect.Icon asChild>
              <Icon
                className={classNames(styles.icon, { [styles.disabled]: disabled })}
                size="24"
                variant="chevron-down"
              />
            </RadixSelect.Icon>
          </RadixSelect.Trigger>

          <RadixSelect.Portal container={portalContainer ?? popoverContainers.general()}>
            <RadixSelect.Content
              className={classNames(styles.content, { [styles.accent]: isAccentBackground })}
              position="popper"
            >
              <RadixScrollArea.Root className={styles.scrollAreaRoot} type="auto">
                <RadixSelect.Viewport asChild>
                  <RadixScrollArea.Viewport className={styles.viewport} style={{ overflowY: undefined }}>
                    <div className={styles.optionsContainer}>
                      {options.map(({ label, value }) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </div>
                  </RadixScrollArea.Viewport>
                </RadixSelect.Viewport>
                <RadixScrollArea.Scrollbar className={styles.scrollBar} orientation="vertical">
                  <RadixScrollArea.Thumb className={styles.thumb} />
                </RadixScrollArea.Scrollbar>
              </RadixScrollArea.Root>
            </RadixSelect.Content>
          </RadixSelect.Portal>
        </RadixSelect.Root>
        {hasInlineNotification ? (
          <div className={styles.inlineNotifContainer}>
            <FormInlineNotification type={inlineNotification.type}>{inlineNotification.message}</FormInlineNotification>
          </div>
        ) : null}
        {hasHelper ? (
          <>
            <Spacer size="03" />
            <FormHelperText disabled={disabled} id={helperId}>
              {helperMsg}
            </FormHelperText>
          </>
        ) : null}
      </div>
    </div>
  );
};

const SelectItem: React.FC<RadixSelect.SelectItemProps> = ({ children, ...props }) => {
  return (
    <RadixSelect.Item asChild {...props}>
      <Radio
        className={styles.radio}
        indicator={
          <RadixSelect.ItemIndicator>
            <RadioIndicator />
          </RadixSelect.ItemIndicator>
        }
      >
        <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
      </Radio>
    </RadixSelect.Item>
  );
};
